import { Suspense } from "react";
import { DashboardAsset, DashboardThread } from "../../types.js";
import { lazyWithRetry } from "../../../../utils/lazyWithRetry";

// Asset details pull in the heavy Three.js viewer stack, and the remaining
// dashboard modals are only needed after explicit user interaction. Loading
// them on demand keeps the admin dashboard route lighter on initial render.
const AssetDetailModal = lazyWithRetry(() =>
  import("./AssetDetailModal").then((module) => ({
    default: module.AssetDetailModal,
  })),
);
const AttachmentModal = lazyWithRetry(() =>
  import("./AttachmentModal").then((module) => ({
    default: module.AttachmentModal,
  })),
);
const ProjectActionModal = lazyWithRetry(() =>
  import("./ProjectActionModal").then((module) => ({
    default: module.ProjectActionModal,
  })),
);
const SecurityModal = lazyWithRetry(() =>
  import("./SecurityModal").then((module) => ({
    default: module.SecurityModal,
  })),
);
const SignOutConfirmModal = lazyWithRetry(() =>
  import("./SignOutConfirmModal").then((module) => ({
    default: module.SignOutConfirmModal,
  })),
);

export type DashboardModalState =
  | { kind: "none" }
  | { kind: "asset"; asset: DashboardAsset }
  | { kind: "project"; projectId: string; projectName: string }
  | { kind: "attachment"; thread: DashboardThread }
  | { kind: "security" }
  | { kind: "signout" };

export function DashboardModalHost({
  modal,
  onClose,
  onNavigateToMessages,
  onSignOut,
}: {
  modal: DashboardModalState;
  onClose: () => void;
  onNavigateToMessages?: (assetContext?: { assetId: string; assetName: string }) => void;
  onSignOut?: () => Promise<void>;
}) {
  switch (modal.kind) {
    case "asset":
      return (
        <Suspense fallback={null} data-oid="-g4109w">
          <AssetDetailModal
            asset={modal.asset}
            onClose={onClose}
            onNavigateToMessages={onNavigateToMessages}
            data-oid="b.pwz48"
          />
        </Suspense>
      );

    case "project":
      return (
        <Suspense fallback={null} data-oid="66o8j3t">
          <ProjectActionModal
            onClose={onClose}
            projectId={modal.projectId}
            projectName={modal.projectName}
            data-oid=".0jny:."
          />
        </Suspense>
      );

    case "attachment":
      return (
        <Suspense fallback={null} data-oid="3fq2h6d">
          <AttachmentModal
            onClose={onClose}
            thread={modal.thread}
            data-oid="92v5:ah"
          />
        </Suspense>
      );

    case "security":
      return (
        <Suspense fallback={null} data-oid="gdglk9g">
          <SecurityModal onClose={onClose} data-oid=".os2rg0" />
        </Suspense>
      );

    case "signout":
      return (
        <Suspense fallback={null}>
          <SignOutConfirmModal
            onClose={onClose}
            onConfirm={onSignOut ?? (async () => undefined)}
          />
        </Suspense>
      );

    case "none":
    default:
      return null;
  }
}
